import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Clock,
  Calendar,
  Settings,
  LogOut,
  User,
  Users,
  Menu,
  X,
  Zap,
  Briefcase,
  BarChart2,
  TrendingUp,
  KeyRound,
} from 'lucide-react';
import ChangePasswordModal from '../Auth/ChangePasswordModal';

const SidebarContent = ({ navigation, isCurrentPath, user, handleLogout, onChangePassword }) => (
  <div className="flex-1 flex flex-col min-h-0 bg-white border-r border-gray-200">
    <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
      <div className="flex items-center flex-shrink-0 px-4">
        <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
          <Clock className="h-5 w-5 text-white" />
        </div>
        <span className="ml-2 text-xl font-semibold text-gray-900">Attendance</span>
      </div>

      <nav className="mt-8 flex-1 px-2 space-y-1">
        {navigation.map((item) => {
          const Icon = item.icon;
          return (
            <a
              key={item.name}
              href={item.href}
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                isCurrentPath(item.href)
                  ? 'bg-blue-100 text-blue-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon className={`mr-3 flex-shrink-0 h-5 w-5 ${
                isCurrentPath(item.href) ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
              }`} />
              {item.name}
            </a>
          );
        })}
      </nav>
    </div>

    <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
      <div className="flex items-center w-full">
        <div className="flex-shrink-0">
          <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center">
            <User className="h-5 w-5 text-gray-600" />
          </div>
        </div>
        <div className="ml-3 flex-1">
          <p className="text-sm font-medium text-gray-700">{user?.name}</p>
          <p className="text-xs text-gray-500">{user?.staff_no}</p>
        </div>
        <button
          onClick={onChangePassword}
          title="Change password"
          className="ml-2 flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <KeyRound className="h-5 w-5" />
        </button>
        <button
          onClick={handleLogout}
          className="ml-1 flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <LogOut className="h-5 w-5" />
        </button>
      </div>
    </div>
  </div>
);

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [appVersion, setAppVersion] = useState('');

  useEffect(() => {
    fetch('/api/version')
      .then(r => r.json())
      .then(d => setAppVersion(d.version))
      .catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Attendance', href: '/attendance', icon: Calendar },
    { name: 'Overtime',   href: '/overtime',   icon: Clock },
  ];

  if (user?.is_manager || user?.is_hr || user?.is_admin) {
    navigation.push({ name: 'Manager',   href: '/manager',   icon: Briefcase });
    navigation.push({ name: 'Analytics', href: '/analytics', icon: TrendingUp });
  }
  if (user?.is_admin) {
    navigation.push({ name: 'Admin Panel', href: '/admin', icon: Settings });
  }
  if (user?.is_admin || user?.is_hr) {
    navigation.push({ name: 'HR Panel',  href: '/hr',        icon: Users });
    navigation.push({ name: 'Reporting', href: '/reporting', icon: BarChart2 });
  }
  if (user?.is_admin) {
    navigation.push({ name: 'Processing', href: '/processing', icon: Zap });
  }

  const isCurrentPath = (path) => location.pathname === path;
  const sidebarProps = {
    navigation,
    isCurrentPath,
    user,
    handleLogout,
    onChangePassword: () => setPasswordModalOpen(true),
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Mobile sidebar */}
        <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'pointer-events-none'}`}>
          <div className={`fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity ease-linear duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setSidebarOpen(false)} />
          <div className={`relative flex-1 flex flex-col max-w-xs w-full bg-white transform transition ease-in-out duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <SidebarContent {...sidebarProps} />
          </div>
        </div>

        {/* Static sidebar for desktop */}
        <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
          <SidebarContent {...sidebarProps} />
        </div>

        {/* Main content */}
        <div className="md:pl-64 flex flex-col flex-1">
          <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-50">
            <button
              type="button"
              className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>

          <main className="flex-1">
            <div className="py-6">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                {children}
              </div>
            </div>
          </main>

          <footer className="md:border-t border-gray-200 py-3 px-4 sm:px-6 md:px-8">
            <p className="text-xs text-gray-400 text-center">
              Attendance System {appVersion && `v${appVersion}`} &mdash; &copy; {new Date().getFullYear()} Histotech Engineering
            </p>
          </footer>
        </div>
      </div>

      {passwordModalOpen && (
        <ChangePasswordModal
          forced={false}
          onClose={() => setPasswordModalOpen(false)}
          onSuccess={() => setPasswordModalOpen(false)}
        />
      )}
    </>
  );
};

export default Layout;
